import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useMemo } from 'react';

import {
  PageLayout,
  SimpleButton,
  ArrowIcon,
  disableUIBlocker,
  BoldHeading,
  Paragraph,
} from '@/components/common';
import { OrderId } from '@/components/common/text/OrderId';
import { RedirectStatus } from '@/components/contexts/params/schemas/RedirectStatus';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast } from '@/libs/toasts';
import PaymentOnDeliveryService from '@/services/delivery/PaymentOnDeliveryService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { formatPrice } from '@/utils/formatters';

import { PayButton } from '../stored-instruments/utils/PayButton';

const styles = {
  loadingSpinner: 'flex items-center justify-center',
  pageContent: 'p-sm md:p-md 2xl:p-lg !pt-0',
  pageContentHeader:
    'sticky top-0 p-sm md:p-md 2xl:p-lg z-50 text-black bg-secondary flex flex-col md:flex-row text-left items-center justify-between md:flex-wrap',
  pageContentNavigation: 'flex',
  pageContentFooter: 'mt-md flex w-full items-center justify-center',
  pageStickyContainer:
    'sticky bottom-0 border-0 z-50 bg-transparent pb-xs pt-sm',
  pageInnerStickyContainer: 'flex justify-center',
  orderId:
    'text-gray-700 text-sm w-full flex justify-between md:w-auto items-center',
};

export function PaymentOnDeliveryDetailsPage() {
  const { accessToken } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  useEffect(() => {
    disableUIBlocker();
  }, []);

  const headerText = useMemo(() => {
    return t('Total: {{totalPrice}}', {
      totalPrice: formatPrice(
        language as SupportedLanguage,
        PaymentStore.currency,
        PaymentStore.orderTotal,
      ),
    });
  }, [t, language]);

  return (
    <PageLayout>
      <div className={styles.pageContentHeader}>
        <BoldHeading extraClassName="text-3xl self-start w-auto">
          {headerText}
        </BoldHeading>
        {PaymentStore.orderNumber && (
          <OrderId
            extraClassName={styles.orderId}
            orderNumber={PaymentStore.orderNumber}
          />
        )}
      </div>
      <div className="flex flex-col items-center text-center m-sm md:m-md p-sm md:p-md bg-white rounded-xl">
        <img
          src="/images/icon-payment-on-delivery.png"
          className="w-[7.5rem]"
          alt={'Payment On Delivery'}
        />
        <BoldHeading extraClassName="text-[1rem] mb-xs">
          {t('Pay for your item when it arrives.')}
        </BoldHeading>
        <Paragraph extraClassName="text-light text-[1rem]">
          {t(
            'Please have the exact amount ready to hand over to the courier upon delivery.',
          )}
        </Paragraph>
      </div>
      <div className={'flex flex-row items-center m-sm md:m-md space-x-2'}>
        <SimpleButton
          className="w-[50px] h-[50px] relative ring-[2px] ring-tertiary rounded-full"
          onClick={() =>
            NavigationService.navigateTo({
              page: InternalPage.PaymentMethods,
            })
          }
        >
          <span className={'flex flex-row items-center justify-center'}>
            <ArrowIcon
              direction="left"
              className="text-tertiary"
            />
          </span>
        </SimpleButton>
        <PayButton
          title={t('Proceed')}
          onClick={() => {
            const currentPage = NavigationService.getInternalPage();
            NavigationService.navigateTo({
              page: InternalPage.StatusProcessing,
            });
            PaymentOnDeliveryService.authorise(accessToken ?? '')
              .then(() => {
                NavigationService.navigateTo({
                  page: InternalPage.PaymentOnDeliveryStatusSuccess,
                });
                Clarity.setTag('pay', [
                  RedirectStatus.Success,
                  paymentModuleT(PaymentModuleId.PaymentOnDelivery),
                ]);
              })
              .catch((err) => {
                NavigationService.navigateTo(currentPage);
                TelemetryService.trackException(
                  err,
                  'PaymentOnDeliveryDetailsPage',
                );
                Logger.error(
                  LoggerOrigin.ReactComponent,
                  'Payment On Delivery failed',
                  err,
                );
                triggerErrorToast(
                  t('Something went wrong!'),
                  t('Failed to authorize Payment On Delivery.'),
                );
                Clarity.setTag('pay', [
                  RedirectStatus.Failure,
                  paymentModuleT(PaymentModuleId.PaymentOnDelivery),
                ]);
              });
          }}
        />
      </div>
    </PageLayout>
  );
}
